import React from 'react';

// Function to generate consistent color from stop ID
const getStopColor = (stopId) => {
  if (!stopId) return '#6B7280'; // Default gray
  
  // Create a hash from the stopId
  let hash = 0;
  for (let i = 0; i < stopId.length; i++) {
    hash = stopId.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate HSL color with good saturation and lightness for visibility
  const hue = Math.abs(hash) % 360;
  const saturation = 65 + (Math.abs(hash) % 20); // 65-85%
  const lightness = 45 + (Math.abs(hash) % 15); // 45-60%
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

function LoopedItinerary({ 
  jobs: itinerary = [], // Renamed for clarity - this is now the calculated itinerary
  nextStep,
  onTripSelect, 
  onStopActionsSelect,
  breakLogs = [],
  endPointLog = null
}) {

  console.log("Rendering LoopedItinerary with itinerary:", itinerary);
  console.log("Rendering LoopedItinerary with nextStep:", nextStep);
  
  // Helper function to check if a stop has a scheduled break
  const hasScheduledBreak = (stopId) => {
    return breakLogs.some(breakLog => breakLog.StopId === stopId);
  };

  // Helper function to check if a stop is the end point
  const isEndPoint = (stopId) => {
    if (!endPointLog) return false;
    return endPointLog.StopId === stopId;
  };

  // Find the current waypoint index
  const currentWaypointIndex = itinerary.findIndex(stop => stop.currentWaypoint);
  
  // Rotate itinerary so that current waypoint is first
  let displayItinerary = [...itinerary];
  if (currentWaypointIndex > 0) {
    displayItinerary = [
      ...itinerary.slice(currentWaypointIndex), 
      ...itinerary.slice(0, currentWaypointIndex)
    ];
  }
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm  flex flex-col">
      
 
      {/* Current Loop Stops */}
      <div className="flex-1">

        
        {displayItinerary.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-lg mb-2">🔄</div>
            <div>Loop completed!</div>
            <div className="text-sm">Preparing next iteration...</div>
          </div>
        ) : (
          <div className="space-y-3">
            {displayItinerary.slice(0).map((stop, index) => {
              // Extract stop properties with fallbacks for different naming conventions
              const stopId = stop.StopId || stop.ItineraryId || stop.Id;
              const stopName = stop.StopName || stop.LandmarkName || stop.Name || 'Unknown Stop';
              const displayTime = stop.eta ||  stop.displayTime || 'N/A';
              const derivedBreakTime = breakLogs[0]?.UpdatedOn 
              ? Math.round((new Date(breakLogs[0].UpdatedOn) - new Date(stop.eta)) / 60000) 
              : null;
              const stopColor = getStopColor(stopId);
              const hasBreak = stop.breakStep || hasScheduledBreak(stopId);
              const isEndStop = stop.endStep || isEndPoint(stopId);
              
              // Determine stop status
              const isCompleted = false;
              const isCurrent = stop.currentWaypoint;
              
              return (
                <div
                  key={`${stopId}_${index}`}
                  className={`
                    border rounded-lg px-3 py-4 cursor-pointer transition-all duration-200
                    ${isCompleted 
                      ? 'border-green-200 bg-green-50' 
                      : isCurrent 
                        ? 'border-blue-200 bg-blue-50' 
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }
                  `}
                  onClick={() => {
                    if ((onStopActionsSelect || !!endPointLog) && (displayItinerary.length / 2)) {
                      const stopData = {
                        stopId: stopId,
                        Id: stopId,
                        Name: stopName,
                        address: stopName,
                        StopName: stopName,
                        LandmarkName: stopName,
                        estimatedArrivalTime: displayTime,
                        arrival: displayTime,
                        displayTime: displayTime,
                        currentWaypoint: isCurrent,
                        completed: isCompleted,
                        ...stop // Include all other properties
                      };
                      onStopActionsSelect(stopData);
                    } else {
                      onTripSelect(index);
                    }
                  }}
                >
                  <div className="flex justify-between items-start text-xl">
                    <div className="flex items-center space-x-3">
                      <span 
                        className="w-6 h-6 rounded-full flex items-center justify-center border-2 border-white shadow-sm"
                        style={{
                          backgroundColor: stopColor,
                          boxShadow: 'none'
                        }}
                      >

                      </span>
                      <div className="flex items-center space-x-2">
                        <div className={`font-semibold text-xl ${
                          isCompleted ? 'text-green-700' : 
                          isCurrent ? 'text-blue-700' : 
                          'text-gray-800'
                        }`}>
                          {stopName}
                        </div>
                        {/* Visual indicators for status, breaks and end points */}
                        <div className="flex items-center space-x-1">
                          {isCompleted && (
                            <span 
                              className="inline-flex items-center justify-center w-5 h-5 text-xs bg-green-100 text-green-600 rounded-full border border-green-200"
                              title="Stop completed"
                            >
                              ✓
                            </span>
                          )}
                          {isCurrent && !isCompleted && (
                            <span 
                              className="inline-flex items-center justify-center w-5 h-5 text-xs bg-blue-100 text-blue-600 rounded-full border border-blue-200"
                              title="Current waypoint"
                            >
                              →
                            </span>
                          )}
                          { (hasBreak  && (index < (displayItinerary.length / 2) ) )&&  (
                            <span 
                              className="inline-flex items-center justify-center w-5 h-5 text-xs bg-orange-100 text-orange-600 rounded-full border border-orange-200"
                              title="Scheduled break at this stop"
                            >
                              ☕
                            </span>
                          )}
                          {isEndStop && (
                            <span 
                              className="inline-flex items-center justify-center w-5 h-5 text-xs bg-red-100 text-red-600 rounded-full border border-red-200"
                              title="Route ends at this stop"
                            >
                              🏁
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  
                    <div className="text-right flex flex-col items-end">
                      <div className={`text-xl font-medium ${
                        isCompleted ? 'text-green-700' : 
                        isCurrent ? 'text-blue-700' : 
                        'text-gray-700'
                      }`}>
                        {new Date(displayTime).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                      
                      {/* Status indicators */}
                      {isCompleted && stop.completedTime && (
                        <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded mt-1">
                          Completed: {new Date(stop.completedTime).toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </div>
                      )}
                      
        
                    </div>
                  </div>

                  {stop.instructions && (
                    <div className="text-sm text-gray-600 mb-2">
                      {stop.instructions}
                    </div>
                  )}
                  
      

                  
                </div>
              )
            })}
          </div>
        )}
      </div>


    </div>
  );
}

export default LoopedItinerary;

// Export the color function for use in other components
export { getStopColor };
